import React, { FunctionComponent } from 'react';
import { cn } from '@companion-professional/webutils';

// The Shepherd icon (a shepherd's crook within a shield) from lib/branding/shepherd/shepherd-icon.svg.  It's inlined so
// the color can follow the surrounding text color and so no asset loader is needed.
const iconPath =
  'M0,0v59.1c0,22.2,17.7,40.2,39.5,40.2h2.6c21.8,0,39.5-18,39.5-40.2V0H0ZM70.7,59.1c0,15.1-11.3,27.5-25.7,28.9l-14.3-' +
  '41.6c-2.8-8.2-.4-14.7,6.3-17.1,5.9-2.1,12.5,1.1,14.6,7.2,1,2.9.8,6.2-.6,9.2-.6,1.3-1.6,2.3-2.6,3.4-3.7,3.9-6.7,8.2-' +
  '4.2,17.7l11.1-1.4c-1.8-5.5-1.1-6.2,1.1-8.6,1.4-1.5,3.2-3.4,4.5-6.3,2.7-5.6,3.1-11.9,1.1-17.7-4.1-11.9-16.9-18.1-' +
  '28.6-14-12.5,4.4-17.8,17.2-13,31.2l12.8,37.3c-12.7-3-22.2-14.5-22.2-28.3V11.1h59.8v47.9h-.1Z';

interface ShepherdIconProps {
  className?: string;

  // tone sets the icon color.  "brand" is Shepherd Blue (the primary logo color), "current" follows the text color
  // (e.g. white on a Navy background).  Per the brand guide the icon is always a single color.
  tone?: 'brand' | 'current';

  // title is used as the accessible name.  When omitted the icon is treated as decorative.
  title?: string;
}

// ShepherdIcon is the Shepherd shield icon.  The brand guide treats it as a secondary logo that may stand alone where
// the name is already known.  Don't stretch it: size it with a height (or width) and let the other dimension be auto.
export const ShepherdIcon: FunctionComponent<ShepherdIconProps> = ({ className, tone = 'brand', title }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 81.6 99.3"
    className={cn('w-auto shrink-0', tone === 'brand' ? 'text-brand-blue' : '', className)}
    fill="currentColor"
    role={title ? 'img' : undefined}
    aria-hidden={title ? undefined : true}
  >
    {title ? <title>{title}</title> : null}
    <path d={iconPath} />
  </svg>
);

interface ShepherdLensLogoProps {
  className?: string;

  // inverted renders the logo for dark backgrounds (e.g. Navy): a white icon and wordmark.
  inverted?: boolean;

  // size controls the overall scale of the lockup.
  size?: 'sm' | 'md' | 'lg';

  // stacked places the wordmark under the icon (e.g. for splash and error screens).
  stacked?: boolean;
}

const sizes = {
  sm: { icon: 'h-6', text: 'text-lg', gap: 'gap-2' },
  md: { icon: 'h-8', text: 'text-xl', gap: 'gap-2.5' },
  lg: { icon: 'h-16 md:h-20', text: 'text-3xl md:text-4xl', gap: 'gap-4' }
};

// ShepherdLensLogo is the Shepherd Lens product lockup: the Shepherd icon in Blue with "Shepherd Lens" set in Spoof.
// On light backgrounds the wordmark is Navy (matching the primary Shepherd logo); inverted renders all white.
export const ShepherdLensLogo: FunctionComponent<ShepherdLensLogoProps> = ({
  className,
  inverted = false,
  size = 'md',
  stacked = false
}) => {
  const s = sizes[size];
  return (
    <div
      className={cn(
        'inline-flex items-center',
        stacked ? 'flex-col' : 'flex-row',
        s.gap,
        inverted ? 'text-white' : 'text-brand-navy',
        className
      )}
    >
      <ShepherdIcon className={s.icon} tone={inverted ? 'current' : 'brand'} />
      <span className={cn('whitespace-nowrap font-display leading-none', s.text)}>Shepherd Lens</span>
    </div>
  );
};
